import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from './icons';
import axios from 'axios';
import { tx } from './i18n';

const API = process.env.REACT_APP_API_URL || '';
const ACTIVE_KEY = 'active_station_profile';

// ── Profile sprzetu (radio + antena + moc) i porownanie anten ────────────────
// Profil wybrany w loggerze kopiuje radio/antene do kazdego nowego QSO
// (my_rig/my_antenna). Wybor jest zapamietany per przegladarka (localStorage)
// -- na innym urzadzeniu zwykle pracujesz z innego zestawu.

const readActive = () => { try { return localStorage.getItem(ACTIVE_KEY) || ''; } catch { return ''; } };
const writeActive = (id) => { try { localStorage.setItem(ACTIVE_KEY, id ? String(id) : ''); } catch { /* brak storage */ } };

// Pasek w loggerze: wybor profilu. `editing` -- podczas edycji QSO pasek jest
// ukryty (edytowane QSO ma swoj zapisany sprzet), a po jej zakonczeniu
// aktywny profil jest nakladany ponownie.
export function StationProfileBar({ form, setForm, editing, onManage }) {
  const [profiles, setProfiles] = useState(null);
  const [activeId, setActiveId] = useState(readActive);
  const wasEditing = useRef(editing);

  useEffect(() => {
    axios.get(`${API}/api/station-profiles`).then(r => setProfiles(r.data)).catch(() => setProfiles([]));
  }, []);

  const apply = useCallback((p) => {
    setForm(f => ({ ...f, my_rig: p ? p.rig : '', my_antenna: p ? p.antenna : '',
      power: p && p.power ? String(p.power) : f.power }));
  }, [setForm]);

  const active = profiles?.find(p => String(p.id) === String(activeId)) || null;

  // Po zaladowaniu profili albo po wyjsciu z edycji -- naloz aktywny profil,
  // o ile formularz nie ma juz tego sprzetu.
  useEffect(() => {
    if (!profiles || editing) { wasEditing.current = editing; return; }
    const leftEdit = wasEditing.current && !editing;
    wasEditing.current = editing;
    if (active && (leftEdit || form.my_rig === undefined || form.my_antenna !== active.antenna || form.my_rig !== active.rig)) {
      apply(active);
    }
    if (!active && activeId) { setActiveId(''); writeActive(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles, editing, active]);

  if (editing || !profiles) return null;
  if (profiles.length === 0) {
    return (
      <div className="station-profile-bar station-profile-bar--empty">
        🎛️ <button type="button" className="btn btn--ghost btn--sm" onClick={onManage}>{tx('Dodaj profil sprzętu')}</button>
        <span>{tx('— radio i antena zapiszą się przy każdym QSO, a potem porównasz anteny.')}</span>
      </div>
    );
  }
  return (
    <div className="station-profile-bar">
      <span>{tx('🎛️ Sprzęt:')}</span>
      <select className="input" value={activeId} onChange={e => {
        const id = e.target.value;
        setActiveId(id); writeActive(id);
        apply(profiles.find(p => String(p.id) === id) || null);
      }}>
        <option value="">{tx('— bez profilu —')}</option>
        {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      {active && (
        <span className="station-profile-bar__desc">
          {[active.rig, active.antenna, active.power ? `${active.power} W` : ''].filter(Boolean).join(' · ')}
        </span>
      )}
    </div>
  );
}

const EMPTY = { name: '', rig: '', antenna: '', power: '' };

export default function StationView({ notify }) {
  const [profiles, setProfiles] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | profile
  const [form, setForm] = useState(EMPTY);
  const [group, setGroup] = useState('antenna');
  const [band, setBand] = useState('');
  const [stats, setStats] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/api/station-profiles`).then(r => setProfiles(r.data)).catch(() => setProfiles([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API}/api/stats/equipment`, { params: { group, band } })
      .then(r => setStats(r.data)).catch(() => setStats(null));
  }, [group, band]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === 'new') await axios.post(`${API}/api/station-profiles`, form);
      else await axios.put(`${API}/api/station-profiles/${editing.id}`, form);
      setEditing(null); load();
    } catch (err) { notify(err.response?.data?.error || tx('Błąd zapisu'), 'error'); }
  };

  const remove = async (p) => {
    if (!window.confirm(tx('Usunąć profil "{0}"? Zapisane QSO zachowają swój sprzęt.', [p.name]))) return;
    try { await axios.delete(`${API}/api/station-profiles/${p.id}`); load(); }
    catch { notify(tx('Błąd usuwania'), 'error'); }
  };

  const rows = stats?.rows || [];
  const best = (key, higher = true) => {
    const vals = rows.map(r => r[key]).filter(v => v !== null && v !== undefined);
    if (vals.length < 2) return null;
    return higher ? Math.max(...vals) : Math.min(...vals);
  };
  const bestS = best('avg_s'), bestDb = best('avg_db'), bestAvgKm = best('avg_km'), bestMaxKm = best('max_km');
  const hl = (v, b) => (b !== null && v === b ? { color: 'var(--accent)', fontWeight: 700 } : undefined);

  return (
    <section className="section">
      <div className="section__header">
        <h2><Icon name="sliders" size={20} /> {tx('Mój sprzęt')}</h2>
        <button className="btn btn--secondary btn--sm" onClick={() => { setEditing('new'); setForm(EMPTY); }}><Icon name="plus" size={16} /> {tx('Nowy profil')}</button>
      </div>
      <div style={{ padding: 16 }}>
        <p style={{ fontSize: 13, color: 'var(--text-dim)', marginTop: 0 }}>
          {tx('Profil to zestaw radio + antena (+ typowa moc). Wybierasz go w loggerze nad polem znaku — sprzęt zapisuje się przy każdym QSO (także w eksporcie ADIF jako MY_RIG / MY_ANTENNA).')}
        </p>

        {editing && (
          <form onSubmit={save} className="panel-box">
            <div className="form-row">
              <div className="form-group"><label>{tx('Nazwa profilu')}</label>
                <input className="input" value={form.name} autoFocus required placeholder={tx('np. Dom — EFHW')}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
              <div className="form-group"><label>{tx('Radio')}</label>
                <input className="input" value={form.rig} placeholder={tx('np. IC-7300')}
                  onChange={e => setForm(f => ({ ...f, rig: e.target.value }))} /></div>
              <div className="form-group"><label>{tx('Antena')}</label>
                <input className="input" value={form.antenna} placeholder={tx('np. EFHW 40–10 m')}
                  onChange={e => setForm(f => ({ ...f, antenna: e.target.value }))} /></div>
              <div className="form-group"><label>{tx('Moc (W)')}</label>
                <input className="input" type="number" min="1" value={form.power ?? ''} placeholder="100"
                  onChange={e => setForm(f => ({ ...f, power: e.target.value }))} /></div>
            </div>
            <div className="form-actions">
              <button className="btn btn--primary btn--sm" type="submit">{editing === 'new' ? tx('Dodaj') : tx('Zapisz')}</button>
              <button className="btn btn--ghost btn--sm" type="button" onClick={() => setEditing(null)}>{tx('Anuluj')}</button>
            </div>
          </form>
        )}

        {profiles === null ? <div style={{ color: 'var(--text-dim)' }}>⏳</div>
          : profiles.length === 0 ? <div className="empty-state">{tx('Nie masz jeszcze profili.')}</div> : (
            <table className="log-table" style={{ marginBottom: 24 }}>
              <thead><tr><th>{tx('Profil')}</th><th>{tx('Radio')}</th><th>{tx('Antena')}</th><th>{tx('Moc')}</th><th></th></tr></thead>
              <tbody>
                {profiles.map(p => (
                  <tr key={p.id} className="log-row">
                    <td><strong>{p.name}</strong></td><td>{p.rig}</td><td>{p.antenna}</td>
                    <td>{p.power ? `${p.power} W` : ''}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn-icon" onClick={() => { setEditing(p); setForm({ ...p, power: p.power ?? '' }); }}><Icon name="edit" size={16} /></button>
                      <button className="btn-icon btn-icon--danger" onClick={() => remove(p)}><Icon name="trash" size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

        <h3 style={{ marginBottom: 6 }}>{tx('📊 Porównanie')} {group === 'rig' ? 'radii' : 'anten'}</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
          <select className="input" style={{ width: 'auto' }} value={group} onChange={e => setGroup(e.target.value)}>
            <option value="antenna">{tx('Anteny')}</option>
            <option value="rig">{tx('Radia')}</option>
          </select>
          <select className="input" style={{ width: 'auto' }} value={band} onChange={e => setBand(e.target.value)}>
            <option value="">{tx('Wszystkie pasma')}</option>
            {(stats?.bands || []).concat(band && !(stats?.bands || []).includes(band) ? [band] : []).map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          {!band && rows.length > 1 && (
            <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{tx('💡 Uczciwe porównanie anten wymaga wybrania jednego pasma.')}</span>
          )}
        </div>
        {rows.length === 0 ? (
          <div className="empty-state">{tx('Brak QSO z zapisanym sprzętem')}{band ? ` na ${band}` : ''}{tx('. Wybierz profil w loggerze i zrób kilka łączności.')}</div>
        ) : (
          <table className="log-table">
            <thead><tr>
              <th>{group === 'rig' ? tx('Radio') : tx('Antena')}</th><th>QSO</th>
              <th title={tx('Średni raport otrzymany (S z RST) — fonia/CW')}>{tx('Śr. raport S')}</th>
              <th title={tx('Średni raport otrzymany w dB — FT8/FT4')}>{tx('Śr. raport dB')}</th>
              <th title={tx('Średnia odległość od Twojego QTH (tylko QSO z lokalizacją)')}>{tx('Śr. km')}</th>
              <th>{tx('Maks. km')}</th><th>{tx('Kraje')}</th>
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.name} className="log-row">
                  <td><strong>{r.name}</strong></td>
                  <td>{r.qsos}</td>
                  <td style={hl(r.avg_s, bestS)}>{r.avg_s ?? '—'}</td>
                  <td style={hl(r.avg_db, bestDb)}>{r.avg_db !== null ? `${r.avg_db > 0 ? '+' : ''}${r.avg_db}` : '—'}</td>
                  <td style={hl(r.avg_km, bestAvgKm)}>{r.avg_km ?? '—'}</td>
                  <td style={hl(r.max_km, bestMaxKm)}>{r.max_km ?? '—'}</td>
                  <td>{r.countries}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p style={{ fontSize: 11, color: 'var(--text-dim)' }}>
          {tx('Raporty zależą też od propagacji i pory dnia — różnice wyjdą wiarygodnie dopiero przy kilkudziesięciu QSO na antenę na tym samym paśmie.')}
        </p>
      </div>
    </section>
  );
}
